import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { api_routes, db_tables, features } from '@app/consts';
import { FeatureService } from '@app/core/services/feature.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { BuySellService } from '@app/modules/time-off/services/buy-sell.service';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SecurityProtectedBase } from '@app/shared/components/security-protected/security-protected';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { defer, forkJoin } from 'rxjs';

@Component({
  selector: 'app-buy-sell-plan-employees',
  templateUrl: './buy-sell-plan-employees.component.html',
  styleUrls: ['./buy-sell-plan-employees.component.scss']
})
export class BuySellPlanEmployeesComponent extends SecurityProtectedBase implements OnInit {
@Input() buySellPlanId: string;

public columns: any[] = [
    { field: "employee", title: "Employee", type: 'employee', localizationCode: 'Employee' },
    { field: "published", title: "Published", localizationCode: 'Published', type: 'chip', chipValue: [true, '',  '', false], chipBooleanText: ['Published', 'Unpublished'] },
    { field: "publishedOn", title: "Published On", type: "date", localizationCode: 'PublishedOn' },
    { field: "publishedBy", title: "Published By", type: "employee", localizationCode: 'PublishedBy' }
  ];

  filterCategories: any[] = [
    { field: 'firstName', title: 'First Name',  dataType: 'String', tableId: "tfi_FirstName" },
    { field: 'lastName', title: 'Last Name', dataType: 'String', tableId: "tfi_LastName" },
    { field: "published", title: "Published", type: "Boolean", dataType: 'Boolean' },
    { field: "publishedOn", title: "Published On", type: "Date", dataType: 'Date' },
  ];

  sortableColumns: any[] = [
    { field: "employee", sortValue: ["employee.FirstName", "employee.LastName"] },
    { field: "published", sortValue: "published" },
    { field: "publishedOn", sortValue: "publishedOn" },
    { field: "publishedBy", sortValue: ["publishedBy.FirstName", "publishedBy.LastName"] }
  ];

  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean = true;
  pageSize: number = 20;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  formValid: any;
  sortString: string;
  isLoadingPermissions: boolean = true;
  filterString: string;
  gridId: string;
  getRequest: any;
  searchValue: string;
  searchFilterString: string;
  dialogRef: any;

  constructor(
    private overlayService: OverlayService,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private router: Router,
    private dialog: MatDialog,
    private buySellService: BuySellService,
    featureService: FeatureService,
  ) {
    super(featureService, features.BUY_SELL);
  }
  
  ngOnInit(): void {
    this.gridId = `buy-sell-plan-employees-${this.buySellPlanId}`;
    if (this.hasFeatureAccess) {
      this.getBuySellPlanEmployees();
    }
  }

  getBuySellPlanEmployees() {
    this.isLoading = true;

    // Combine filterString and searchFilterString if both exist
    let baseFilter = `(plan.id = "${this.buySellPlanId}")`;
    let combinedFilter;

    if (this.filterString && this.searchFilterString) {
      combinedFilter = `${baseFilter} AND (${this.filterString}) AND (${this.searchFilterString})`;
    } else if (this.filterString) {
      combinedFilter = `${baseFilter} AND (${this.filterString})`;
    } else if (this.searchFilterString) {
      combinedFilter = `${baseFilter} AND (${this.searchFilterString})`;
    } else {
      combinedFilter = baseFilter;
    }
    // Use combinedFilter in the service call

    this.getRequest = this.buySellService.getBuySellPlanEmployees(this.pageSize, this.skip, combinedFilter, this.sortString)
      .subscribe(
        {
          next: (v) => {
            this.gridDataResult = {
              data: v.data,
              total: v.total
            };

            this.isLoading = false;
          },
          error: (e) => this.isLoading = false,
          complete: () => this.isLoading = false
        }
      );
  }

  openAddEmployeesDialog() {
    let formData = {
      filter: null
    }

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formTitle: `${this.translate.instant('AddEmployeesToBuySellPlan')}`,
      formId: 'frm_0qXNukYkSjve94',
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.addEmployees(event);
      sub.unsubscribe();
    });
  }

  addEmployees(event) {
    this.overlayService.show();

    this.buySellService.addBuySellPlanEmployees(this.buySellPlanId, event.filter)
      .subscribe(
        {
          next: (v) => {
            this.snackbarService.openSnackBar(`${this.translate.instant('EmployeesAddedSuccessfully')}`, 'clear', 'success');
            this.getBuySellPlanEmployees();
            this.dialogRef.close();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  openConfirmDeleteDialog(employeeIds: string[]) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      text: this.translate.instant('AreYouSureYouWantToDeleteThis')
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.deleteBuySellEmployees(employeeIds);
        }
      }
    );
  }

  deleteBuySellEmployees(employeeIds: string[]) {
    const observables = employeeIds.map(selectedItem => defer(() => this.buySellService.deleteBuySellPlanEmployee(selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
      .subscribe(
        {
          next: (v) => {
            this.clearSelectedItems = !this.clearSelectedItems;
            this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
            this.getBuySellPlanEmployees();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if (this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
        if (variant) {
          if (this.searchFilterString.length > 2) {
            this.searchFilterString += ' AND ';
          }
          this.searchFilterString += `(Employee.FirstName like "${variant}") OR (Employee.LastName like "${variant}")`;
        }
      });

      this.skip = 0;
    }

    this.getRequest.unsubscribe();
    this.getBuySellPlanEmployees();
  }

}
